<template>
  <v-navigation-drawer
    v-model="isVisible"
    right
    width="560"
    fixed
    temporary
    class="create-source-drawer">
    <!-- Drawer Header -->
    <div class="drawer-header">
      <div class="header-title">
        <h2 class="title-text">
          {{ drawerTitle }}
        </h2>
        <v-btn
          icon
          variant="text"
          size="medium"
          class="close-btn"
          @click="closeDrawer">
          <Icon name="cross" variant="solid" size="20" color="default"/>
        </v-btn>
      </div>
    </div>

    <!-- Drawer Content -->
    <div class="drawer-content">
      <div class="form-inputs">
        <!-- Collection Selection -->
        <div class="input-group">
          <label class="input-label required">
            Collection
            <span class="required-asterisk">*</span>
          </label>
          <MankSelect
            v-model="formData.collection"
            :items="collections"
            :loading="collectionsLoading"
            placeholder="Select Collection"
            class="collection-select"
            :menu-props="{
              maxHeight:300,
              offsetY:true,
              nudgeBottom:8,
            }"
            :error-messages="errors.collection"
            hide-details="auto"
            item-text="name"
            item-value="id">
            <template v-slot:append>
              <Icon name="expand-down" variant="solid" color="muted"/>
            </template>
          </MankSelect>
        </div>

        <!-- Source Name -->
        <div class="input-group">
          <label class="input-label required">
            Source Name
            <span class="required-asterisk">*</span>
          </label>
          <MankTextField
            v-model="formData.sourceName"
            placeholder="Enter Source Name"
            class="form-input"
            :error-messages="errors.sourceName"
            hide-details="auto"/>
        </div>

        <!-- Description -->
        <div class="input-group">
          <label class="input-label">Description</label>
          <MankTextarea
            v-model="formData.description"
            placeholder="Enter Source Description"
            variant="outlined"
            :error-messages="errors.description"
            hide-details="auto"
            rows="4"
            class="description-textarea"/>
          <div class="helper-text" :class="{ 'error-text': formData.description.length > 300 }">
            {{ formData.description.length }}/300
          </div>
        </div>

        <!-- Source Type Toggle -->
        <div class="input-group">
          <div class="source-type-toggle">
            <v-btn
              :class="['toggle-btn', { active: formData.sourceType === 'web' }]"
              @click="formData.sourceType = 'web'">
              Web
            </v-btn>
            <v-btn
              :class="['toggle-btn', { active: formData.sourceType === 'document' }]"
              @click="formData.sourceType = 'document'">
              Document
            </v-btn>
          </div>
        </div>

        <!-- Web Source Fields -->
        <div v-if="formData.sourceType === 'web'" class="web-fields">
          <!-- Crawl Depth -->
          <div class="input-group">
            <label class="input-label">Crawl Depth</label>
            <p class="field-description">
              Defines the number of link levels from the start URL that will be followed and included in the collection.
            </p>
            <div class="slider-group">
              <v-slider
                v-model="formData.crawlDepth"
                :min="0"
                :max="6"
                :step="1"
                color="foreground-3"
                class="crawl-slider"
                hide-details/>
              <div class="slider-value-input">
                <v-text-field
                  v-model="formData.crawlDepth"
                  outlined
                  class="value-input"
                  hide-details
                  :min="0"
                  :max="6"
                  type="number"
                  step="1"/>
              </div>
            </div>
          </div>

          <!-- URL Section -->
          <div class="url-section">
            <div class="input-group">
              <label class="input-label required">
                URL
                <span class="required-asterisk">*</span>
              </label>
              <MankTextField
                v-model="formData.url"
                placeholder="https://"
                class="form-input"
                :error-messages="errors.url"
                hide-details="auto"/>
            </div>

            <!-- Add URL Button -->
            <div class="add-url-btn" @click="addUrl">
              <Icon name="plus" variant="solid" size="20" color="default"/>
              <span class="add-url-text">Add URL</span>
            </div>

            <!-- URL List -->
            <div v-if="urlList.length > 0" class="url-list">
              <div
                v-for="(urlItem, index) in urlList"
                :key="urlItem.id"
                class="url-item">
                <div class="url-content">
                  <Icon name="plus" variant="solid" size="20" color="default"/>
                  <span class="url-text">{{ urlItem.url }}</span>
                </div>
                <button
                  class="remove-url-btn"
                  @click="removeUrl(index)"
                  type="button">
                  <Icon name="trash" variant="solid" size="20" color="default"/>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Upload Fields -->
        <div v-if="formData.sourceType === 'document'" class="document-fields">
          <div class="document-upload-area">
            <div
              class="drag-drop-area"
              @dragover.prevent
              @drop.prevent="handleFileDrop"
              @click="triggerFileUpload">
              <!-- RAG Icon -->
              <div class="upload-icon">
                <div class="icon-circle">
                  <Icon name="rag" variant="solid" size="24" color="primary"/>
                </div>
              </div>

              <!-- Text Content -->
              <div class="upload-text">
                <h3 class="upload-title">
                  Drag & Drop or Upload File
                </h3>
                <p class="upload-description">
                  txt, pdf, html, doc, docx, epub, md files accepted
                </p>
              </div>

              <!-- Upload Button -->
              <v-btn
                variant="outlined"
                size="medium"
                class="upload-btn"
                elevation="0">
                <Icon name="upload" variant="solid" size="20" color="primary"/>
                Upload File
              </v-btn>

              <!-- Hidden File Input -->
              <input
                ref="fileInput"
                type="file"
                multiple
                accept=".txt,.pdf,.html,.doc,.docx,.epub,.md"
                style="display: none"
                @change="handleFileSelect">
            </div>

            <!-- Uploaded Files List -->
            <div v-if="uploadedFiles.length > 0" class="uploaded-files">
              <div
                v-for="(file, index) in uploadedFiles"
                :key="index"
                class="file-item">
                <div class="file-info">
                  <Icon name="text-box" variant="solid" size="20" color="default"/>
                  <span class="file-name">{{ file.name }}</span>
                </div>
                <button
                  class="remove-file-btn"
                  @click="removeFile(index)"
                  type="button">
                  <Icon name="trash" variant="solid" size="16" color="default"/>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Drawer Footer -->
    <div class="drawer-footer">
      <v-btn
        variant="text"
        size="medium"
        class="cancel-btn"
        elevation="0"
        @click="cancel">
        <Icon name="cross" variant="solid" size="20" color="default"/>
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        size="medium"
        class="create-btn"
        elevation="0"
        :loading="loading"
        :disabled="!isFormValid || loading"
        @click="createSource">
        <Icon :name="isEditMode ? 'check' : 'plus'" variant="solid" size="20" color="onPrimary"/>
        {{ submitButtonText }}
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
import Icon from '@/components/Icon.vue';
import CollectionService from '../services/CollectionService';
import RagService from '../services/RagService';

export default {
  name: 'CreateSourceDrawer',
  components: { Icon },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    collectionId: {
      type: String,
      default: null,
    },
    source: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:visible', 'source-created', 'source-updated'],
  data() {
    return {
      loading: false,
      collectionsLoading: false,
      formData: {
        collection: null,
        sourceName: '',
        description: '',
        sourceType: 'web', // 'web' or 'document'
        crawlDepth: 0,
        url: '',
      },
      urlList: [],
      collections: [],
      uploadedFiles: [],
      errors: {
        collection: [],
        sourceName: [],
        description: [],
        url: [],
        files: [],
      },
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    isFormValid() {
      return (
        this.formData.collection
        && this.formData.sourceName.trim()
        && ((this.formData.sourceType === 'web' && (this.formData.url.trim() || this.urlList.length > 0))
         || (this.formData.sourceType === 'document' && this.uploadedFiles.length > 0))
      );
    },
    isEditMode() {
      return this.source !== null;
    },
    drawerTitle() {
      return this.isEditMode ? 'Edit Source' : 'Create Source';
    },
    submitButtonText() {
      return this.isEditMode ? 'Update' : 'Create';
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchCollections();
        this.setCollectionFromProp();
        this.resetForm();
        if (this.isEditMode) {
          this.populateForm();
        }
      }
    },
    source: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.populateForm();
        }
      },
      deep: true,
    },
  },
  methods: {
    async fetchCollections() {
      this.collectionsLoading = true;
      try {
        const response = await CollectionService.getAllCollections();
        this.collections = (response.data || response || []).map((collection) => ({
          id: collection.id,
          name: collection.name,
        }));
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to load collections',
        });
        this.collections = [];
      } finally {
        this.collectionsLoading = false;
      }
    },

    setCollectionFromProp() {
      if (this.collectionId && this.collections.length > 0) {
        const collection = this.collections.find((c) => c.id === this.collectionId);
        if (collection) {
          this.formData.collection = collection.id;
        }
      }
    },

    resetForm() {
      this.formData = {
        collection: this.collectionId || null,
        sourceName: '',
        description: '',
        sourceType: 'web',
        crawlDepth: 0,
        url: '',
      };
      this.urlList = [];
      this.uploadedFiles = [];
      this.errors = {
        collection: [],
        sourceName: [],
        description: [],
        url: [],
        files: [],
      };
    },

    populateForm() {
      if (this.source) {
        this.formData = {
          collection: this.source.collection_id || this.collectionId,
          sourceName: this.source.name || '',
          description: this.source.description || '',
          sourceType: this.source.source_type || 'web',
          crawlDepth: this.source.crawl_depth || 0,
          url: '',
        };

        // Populate URLs if it's a web source
        if (this.source.source_type === 'web' && this.source.urls) {
          this.urlList = this.source.urls.map((url, index) => ({
            id: Date.now() + index,
            url,
          }));
        }

        // Note: We can't populate uploaded files as they're not stored in the source object
        // User will need to re-upload files if editing a document source
        this.uploadedFiles = [];

        this.errors = {
          collection: [],
          sourceName: [],
          description: [],
          url: [],
          files: [],
        };
      }
    },

    closeDrawer() {
      this.isVisible = false;
    },

    cancel() {
      this.closeDrawer();
    },

    validateForm() {
      this.errors = {
        collection: [],
        sourceName: [],
        description: [],
        url: [],
        files: [],
      };

      let isValid = true;

      // Collection validation
      if (!this.formData.collection) {
        this.errors.collection.push('Collection is required');
        isValid = false;
      }

      // Source name validation
      if (!this.formData.sourceName.trim()) {
        this.errors.sourceName.push('Source name is required');
        isValid = false;
      } else if (this.formData.sourceName.trim().length < 2) {
        this.errors.sourceName.push('Source name must be at least 2 characters');
        isValid = false;
      }

      // Description validation
      if (this.formData.description.length > 300) {
        this.errors.description.push('Description cannot exceed 300 characters');
        isValid = false;
      }

      // Source type specific validation
      if (this.formData.sourceType === 'web') {
        if (!this.formData.url.trim() && this.urlList.length === 0) {
          this.errors.url.push('At least one URL is required for web sources');
          isValid = false;
        } else if (this.formData.url.trim() && !this.isValidUrl(this.formData.url)) {
          this.errors.url.push('Please enter a valid URL');
          isValid = false;
        }
      } else if (this.formData.sourceType === 'document') {
        if (this.uploadedFiles.length === 0) {
          this.errors.files.push('At least one document is required');
          isValid = false;
        }
      }

      return isValid;
    },

    isValidUrl(url) {
      try {
        const validUrl = new URL(url);
        return !!validUrl;
      } catch {
        return false;
      }
    },

    async createSource() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;

      try {
        const formData = new FormData();

        // Required fields
        formData.append('name', this.formData.sourceName.trim());
        formData.append('collection_id', this.formData.collection);
        formData.append('source_type', this.formData.sourceType);

        // Optional description
        if (this.formData.description.trim()) {
          formData.append('description', this.formData.description.trim());
        }

        // Source type specific data
        if (this.formData.sourceType === 'web') {
          // Collect all URLs (main URL + additional URLs from list)
          const allUrls = [];

          if (this.formData.url.trim()) {
            allUrls.push(this.formData.url.trim());
          }

          this.urlList.forEach((urlItem) => {
            allUrls.push(urlItem.url);
          });

          // Add URLs array
          allUrls.forEach((url) => {
            formData.append('urls', url);
          });

          if (this.formData.crawlDepth) {
            formData.append('crawl_depth', this.formData.crawlDepth.toString());
          }
        } else if (this.formData.sourceType === 'document') {
          // Add uploaded files
          this.uploadedFiles.forEach((file) => {
            formData.append('files', file);
          });
        }

        let response;
        let successMessage;
        let eventName;

        if (this.isEditMode) {
          response = await RagService.updateRag(this.source.id, formData);
          successMessage = 'Source updated successfully';
          eventName = 'source-updated';
        } else {
          response = await RagService.createRag(formData);
          successMessage = 'Source created successfully';
          eventName = 'source-created';
        }

        // Success notification
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: successMessage,
        });

        // Emit event to parent component
        const sourceResult = this.isEditMode
          ? {
            ...this.source,
            name: this.formData.sourceName.trim(),
            description: this.formData.description.trim(),
            source_type: this.formData.sourceType,
            ...(response.data || response),
            updated_at: new Date().toISOString(),
          }
          : response.data || response;

        this.$emit(eventName, sourceResult);

        // Close drawer
        this.closeDrawer();
      } catch (error) {
        let errorMessage = this.isEditMode ? 'Failed to update source' : 'Failed to create source';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
      } finally {
        this.loading = false;
      }
    },

    addUrl() {
      if (this.formData.url.trim() && this.isValidUrl(this.formData.url)) {
        // Add URL to the list
        this.urlList.push({
          id: Date.now(),
          url: this.formData.url.trim(),
        });

        // Clear the input field
        this.formData.url = '';

        // Clear any URL errors
        this.errors.url = [];

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'URL added successfully',
        });
      } else {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Please enter a valid URL',
        });
      }
    },

    removeUrl(index) {
      this.urlList.splice(index, 1);
      this.$store.dispatch('ui/showNotification', {
        type: 'success',
        title: 'Success',
        message: 'URL removed successfully',
      });
    },

    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
    },

    handleFileDrop(event) {
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },

    processFiles(files) {
      // Filter accepted file types
      const acceptedTypes = ['.txt', '.pdf', '.html', '.doc', '.docx', '.epub', '.md'];
      const validFiles = files.filter((file) => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return acceptedTypes.includes(extension);
      });

      if (validFiles.length !== files.length) {
        this.$store.dispatch('ui/showNotification', {
          type: 'warning',
          title: 'Invalid Files',
          message: 'Some files were not accepted. Only txt, pdf, html, doc, docx, epub, md files are allowed.',
        });
      }

      // Add valid files to uploaded files list
      this.uploadedFiles = [...this.uploadedFiles, ...validFiles];

      // Clear any previous file errors
      this.errors.files = [];

      if (validFiles.length > 0) {
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Files Added',
          message: `${validFiles.length} file(s) added successfully`,
        });
      }
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.create-source-drawer {
  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.drawer-header {
  padding: 12px 24px;
  border-bottom: 1px solid #D3DAE0;
  background-color: #FFFFFF;

  .header-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;

    .title-text {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: $h5-size;
      line-height: 1.4;
      color: #171D22;
      margin: 0;
    }

    .close-btn {
      width: 32px;
      height: 32px;
      background-color: #F7F8FB;
      border-radius: 8px;

      &:hover {
        background-color: #E9EDF1;
      }
    }
  }
}

.drawer-content {
  flex: 1;
  overflow-y: auto;

  .form-inputs {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .input-label {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    font-weight: 600;
    color: #171D22;
    line-height: 1.2;

    &.required {
      display: flex;
      align-items: center;
      gap: 4px;

      .required-asterisk {
        color: #EB575D;
        font-weight: 700;
      }
    }
  }

  .field-description {
    margin: 0;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    font-weight: 400;
    color: #5C6A75;
    line-height: 1.6;
  }
}

.helper-text {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $caption-1-size;
  font-weight: 400;
  color: #93A0AF;
  text-align: right;

  &.error-text {
    color: #f44336;
  }
}

// Source Type Toggle
.source-type-toggle {
  display: flex;
  background-color: #E9EDF1;
  border-radius: 12px;
  padding: 4px;
  height: 32px;

  .toggle-btn {
    flex: 1;
    height: 24px;
    border-radius: 8px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    font-weight: 600;
    color: #93A0AF;
    background-color: transparent;
    text-transform: none;
    box-shadow: none;

    &.active {
      background-color: #FFFFFF;
      color: #171D22;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.5);
    }
  }
}

// Slider Group
.slider-group {
  display: flex;
  align-items: center;
  gap: 12px;

  .crawl-slider {
    flex: 1;

    :deep(.v-slider-track__fill) {
      background-color: #5C6A75;
    }

    :deep(.v-slider-thumb__surface) {
      background-color: #FFFFFF;
      border: 2px solid #5C6A75;
    }
  }

  .slider-value-input {
    width: 48px;

    .value-input {
      :deep(.v-field__field) {
        background-color: #FFFFFF;
        border: 1px solid #D3DAE0;
        border-radius: 8px;
        min-height: 32px;
      }

      :deep(.v-field__input) {
        text-align: center;
        padding: 6px 4px;
      }
    }
  }
}

// URL Section
.url-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.add-url-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #FFFFFF;
  border: 1px dashed #D3DAE0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #EB575D;
    background-color: rgba(235, 87, 93, 0.02);
  }

  .add-url-text {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    font-weight: 600;
    color: #171D22;
  }
}

.url-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.url-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: #FFFFFF;
  border: 1px solid #D3DAE0;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E9EDF1;
  }

  .url-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;

    .url-text {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-3-size;
      font-weight: 600;
      color: #171D22;
      word-break: break-all;
    }
  }

  .remove-url-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: #F7F8FB;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #fee;

      :deep(.icon) {
        color: #f44336 !important;
      }
    }
  }
}

// Document Upload Area
.document-upload-area {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.drag-drop-area {
  background-color: #F7F8FB;
  border: 2px dashed #D3DAE0;
  border-radius: 12px;
  padding: 12px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #EB575D;
    background-color: rgba(235, 87, 93, 0.02);
  }

  .upload-icon {
    .icon-circle {
      width: 48px;
      height: 48px;
      background-color: #FFFFFF;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .upload-text {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .upload-title {
      margin: 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-2-size;
      font-weight: 700;
      color: #171D22;
      line-height: 1.4;
    }

    .upload-description {
      margin: 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $caption-1-size;
      font-weight: 400;
      color: #5C6A75;
      line-height: 1.6;
    }
  }

  .upload-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    font-weight: 600;
    text-transform: none;
    border: 1px solid #EB575D;
    color: #EB575D;

    &:hover {
      background-color: rgba(235, 87, 93, 0.1);
    }
  }
}

.uploaded-files {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #F7F8FB;
  border-radius: 8px;

  .file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;

    .file-name {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-3-size;
      font-weight: 600;
      color: #171D22;
    }
  }

  .remove-file-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #fee;

      :deep(.icon) {
        color: #f44336 !important;
      }
    }
  }
}

// Drawer Footer
.drawer-footer {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid #D3DAE0;
  background-color: #FFFFFF;

  .cancel-btn,
  .create-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 32px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    font-weight: 600;
    text-transform: none;
    flex: 1;
    border-radius: 8px;
  }

  .cancel-btn {
    background-color: #F7F8FB;
    color: #171D22;

    &:hover {
      background-color: #E9EDF1;
    }
  }

  .create-btn {
    background-color: #EB575D;
    color: #FFFFFF;

    &:hover {
      background-color: darken(#EB575D, 10%);
    }
  }
}
// Vuetify overrides
.mank-input,
.mank-textarea,
.mank-select {
  :deep(.v-field__field) {
    background-color: #F7F8FB;
  }

  :deep(.v-field__input) {
    color: #171D22;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    font-weight: 400;
  }

  :deep(.v-field__outline) {
    --v-field-border-opacity: 0;
  }
}

.collection-select {
  :deep(.v-select__selection-text) {
    color: #93A0AF;
  }
}

.description-textarea {
  :deep(.v-field__field) {
    min-height: 120px;
  }
}
</style>
